/**
 * Tetromino movement queue — the state machine that orders all moves
 * (horizontal, vertical, rotate, hard-drop, place, explode, cleanup)
 * so they don't race against each other or against the placement
 * round-trip with the server.
 *
 * Every public action below pushes one operation onto the queue and
 * lets the asynchronous processor handle rendering side effects in
 * order.  Direct calls (e.g. from `enhanced-gameCore.js`) should
 * always go through this module rather than mutating `gameState`
 * directly.
 */

import { boardFunctions } from '../boardFunctions.js';
import { showToastMessage } from '../showToastMessage.js';
import { displaySponsorInfo } from '../../utils/sponsors.js';
import {
	showDropAnimation,
	showExplosionAnimation,
	showSandDissolveFallAnimation,
	showPlacementEffect,
} from './animations.js';
import { checkTetrominoCollision } from './validation.js';
import {
	renderTetromino,
	cleanupCurrentTetromino,
	cleanupGhostPiece,
} from './rendering.js';
import {
	sendTetrominoPlacementToServer,
	setPlacementFailureHandler,
} from './network.js';
import { initializeNextTetromino } from './spawn.js';
import {
	armSkipChessTimer,
	cancelSkipChessTimer,
	cancelSkipDropTimer,
} from '../skipChessButton.js';
import { updateNextPieceHint } from './nextPiece.js';

export const MOVEMENT_TYPES = Object.freeze({
	MOVE_X: 'moveX',
	MOVE_Z: 'moveZ',
	MOVE_Y: 'moveY',
	ROTATE: 'rotate',
	HARD_DROP: 'hardDrop',
	PLACE: 'place',
	EXPLODE: 'explode',
	CLEANUP: 'cleanup',
});

export const FAILURE_EFFECTS = Object.freeze({
	EXPLODE: 'explode',
	DISSOLVE_FALL: 'dissolve_fall',
});

let _gameStateRef = null;

function ensureQueueState(gameState) {
	if (!Array.isArray(gameState.tetrominoMovementQueue)) {
		gameState.tetrominoMovementQueue = [];
	}
}

export function setGameStateRef(gameState) {
	_gameStateRef = gameState;
	ensureQueueState(gameState);
}

function getGameState() {
	return _gameStateRef;
}

function queueOperation(type, params = {}) {
	const gameState = getGameState();
	if (!gameState) {
		console.warn(`queueTetrominoMovement(${type}) called before gameState was set`);
		return false;
	}
	ensureQueueState(gameState);

	if (!gameState.currentTetromino && type !== MOVEMENT_TYPES.CLEANUP) {
		console.log(`Cannot queue ${type} operation - no tetromino exists`);
		return false;
	}
	if (gameState.turnPhase !== 'tetris' && type !== MOVEMENT_TYPES.CLEANUP) {
		console.log(`Cannot queue ${type} operation - not in tetris phase`);
		return false;
	}

	gameState.tetrominoMovementQueue.push({ type, params, timestamp: Date.now() });

	if (!gameState.isProcessingMovementQueue) {
		setTimeout(processQueue, 0);
	}
	return true;
}

function processQueue() {
	const gameState = getGameState();
	if (!gameState) return;
	if (gameState.isProcessingMovementQueue) return;
	if (!gameState.tetrominoMovementQueue || gameState.tetrominoMovementQueue.length === 0) return;

	gameState.isProcessingMovementQueue = true;
	try {
		while (gameState.tetrominoMovementQueue.length > 0) {
			const operation = gameState.tetrominoMovementQueue.shift();
			if (!gameState.currentTetromino && operation.type !== MOVEMENT_TYPES.CLEANUP) {
				console.log(`Skipping ${operation.type} operation - no tetromino exists`);
				continue;
			}
			if (gameState.turnPhase !== 'tetris' && operation.type !== MOVEMENT_TYPES.CLEANUP) {
				console.log(`Skipping ${operation.type} operation - not in tetris phase`);
				continue;
			}

			switch (operation.type) {
				case MOVEMENT_TYPES.MOVE_X: processHorizontalMove(true, operation.params.dir); break;
				case MOVEMENT_TYPES.MOVE_Z: processHorizontalMove(false, operation.params.dir); break;
				case MOVEMENT_TYPES.MOVE_Y: processVerticalMove(operation.params.height, operation.params.isRelative); break;
				case MOVEMENT_TYPES.ROTATE: processRotate(operation.params.dir); break;
				case MOVEMENT_TYPES.HARD_DROP: processHardDrop(); break;
				case MOVEMENT_TYPES.PLACE: processPlaceTetromino(); break;
				case MOVEMENT_TYPES.EXPLODE:
					processExplosion(
						operation.params.x,
						operation.params.z,
						operation.params.message,
						operation.params.effect || FAILURE_EFFECTS.EXPLODE,
					);
					break;
				case MOVEMENT_TYPES.CLEANUP: processCleanup(operation.params.message); break;
				default: console.warn(`Unknown operation type: ${operation.type}`);
			}
		}

		if (gameState.currentTetromino && gameState.pendingRender) {
			renderTetromino(gameState);
			gameState.pendingRender = false;
		}
	} catch (error) {
		console.error('Error processing movement queue:', error);
	} finally {
		gameState.isProcessingMovementQueue = false;
	}
}

function processHorizontalMove(isXAxis, dir) {
	const gameState = getGameState();
	if (!gameState.currentTetromino) return;

	const newPos = {
		x: isXAxis ? gameState.currentTetromino.position.x + dir : gameState.currentTetromino.position.x,
		z: isXAxis ? gameState.currentTetromino.position.z : gameState.currentTetromino.position.z + dir,
	};
	gameState.currentTetromino.position = newPos;
	gameState.pendingRender = true;
}

function processVerticalMove(height, isRelative) {
	const gameState = getGameState();
	if (!gameState.currentTetromino) return;

	let targetHeight = isRelative
		? gameState.currentTetromino.heightAboveBoard + height
		: height;
	if (targetHeight > gameState.TETROMINO_START_HEIGHT) targetHeight = gameState.TETROMINO_START_HEIGHT;
	if (targetHeight < 0) targetHeight = 0;

	if (targetHeight === 0) {
		const shape = gameState.currentTetromino.shape;
		const posX = gameState.currentTetromino.position.x;
		const posZ = gameState.currentTetromino.position.z;

		for (let z = 0; z < shape.length; z++) {
			for (let x = 0; x < shape[z].length; x++) {
				if (shape[z][x] !== 1) continue;
				const cell = gameState.board?.cells?.[`${posX + x},${posZ + z}`];
				if (cell !== undefined && cell !== null) {
					console.log(`Vertical move failed - collision at Y=0 with existing piece at (${posX + x}, ${posZ + z})`);
					gameState.currentTetromino.heightAboveBoard = 0;
					queueOperation(MOVEMENT_TYPES.EXPLODE, {
						x: posX, z: posZ,
						message: 'Collision on landing',
					});
					return;
				}
			}
		}

		const isAdjacent = boardFunctions.isTetrominoAdjacentToExistingCells(gameState, shape, posX, posZ);
		if (!isAdjacent) {
			console.log('Vertical move failed - tetromino not adjacent at landing position (Y=0)');
			gameState.currentTetromino.heightAboveBoard = 0;
			queueOperation(MOVEMENT_TYPES.EXPLODE, {
				x: posX, z: posZ,
				message: 'Missed connection — pieces must land touching your territory. Watch the outline: green sticks, red dissolves.',
				effect: FAILURE_EFFECTS.DISSOLVE_FALL,
			});
			return;
		}

		console.log('Vertical move successful - landed at Y=0 adjacently. Queueing PLACE.');
		gameState.currentTetromino.heightAboveBoard = 0;
		gameState.pendingRender = true;
		queueOperation(MOVEMENT_TYPES.PLACE, {});
		return;
	}

	gameState.currentTetromino.heightAboveBoard = targetHeight;
	gameState.pendingRender = true;
}

function processRotate(dir) {
	const gameState = getGameState();
	if (!gameState.currentTetromino) return;

	const currentShape = gameState.currentTetromino.shape;
	const size = currentShape.length;
	const newShape = [];
	for (let i = 0; i < size; i++) newShape.push(new Array(size).fill(0));

	for (let z = 0; z < size; z++) {
		for (let x = 0; x < size; x++) {
			if (dir === 1) newShape[x][size - 1 - z] = currentShape[z][x];
			else newShape[size - 1 - x][z] = currentShape[z][x];
		}
	}

	gameState.currentTetromino.shape = newShape;
	// Keep the rotation index in lockstep with the matrix mutation so
	// the server (which rebuilds the shape from `(type, rotation)`)
	// validates the same cells the player sees. `dir === 1` is a CW
	// turn matching the `TETROMINO_SHAPES[type][rotation+1]` step.
	const previousRotation = Number.isInteger(gameState.currentTetromino.rotation)
		? gameState.currentTetromino.rotation
		: 0;
	const step = dir === 1 ? 1 : 3;
	gameState.currentTetromino.rotation = (previousRotation + step) % 4;
	gameState.pendingRender = true;
}

function processHardDrop() {
	const gameState = getGameState();
	if (!gameState.currentTetromino) return;

	console.log('Processing hard drop: Setting Y=0');
	gameState.currentTetromino.heightAboveBoard = 0;
	showDropAnimation(gameState);
	gameState.pendingRender = true;

	const shape = gameState.currentTetromino.shape;
	const posX = gameState.currentTetromino.position.x;
	const posZ = gameState.currentTetromino.position.z;

	if (checkTetrominoCollision(gameState, shape, posX, posZ)) {
		console.log(`Hard drop failed - collision at (${posX}, ${posZ})`);
		queueOperation(MOVEMENT_TYPES.EXPLODE, { x: posX, z: posZ, message: 'Collision on landing' });
		return;
	}

	const isAdjacent = boardFunctions.isTetrominoAdjacentToExistingCells(gameState, shape, posX, posZ);
	if (!isAdjacent) {
		console.log('Hard drop failed - tetromino not adjacent at landing position');
		queueOperation(MOVEMENT_TYPES.EXPLODE, {
			x: posX, z: posZ,
			message: 'Missed connection — pieces must land touching your territory. Watch the outline: green sticks, red dissolves.',
			effect: FAILURE_EFFECTS.DISSOLVE_FALL,
		});
		return;
	}

	console.log('Hard drop successful - landed adjacently. Queueing PLACE.');
	queueOperation(MOVEMENT_TYPES.PLACE, {});
}

function rejectionMessageForReason(response) {
	let rejectionMessage = 'Missed drop - tetromino dissolved into sand.';
	let rejectionEffect = FAILURE_EFFECTS.EXPLODE;
	const reason = response?.reason;
	let message = response?.message;

	if (!message && reason) {
		switch (reason) {
			case 'occupied': message = 'That space is already occupied.'; break;
			case 'not_adjacent':
				message = 'Missed connection — pieces must land touching your territory. Watch the outline: green sticks, red dissolves.';
				rejectionEffect = FAILURE_EFFECTS.DISSOLVE_FALL;
				break;
			case 'no_path_to_king':
				message = 'Each block must connect through your cells back to your king — you cannot bridge across only enemy territory.';
				rejectionEffect = FAILURE_EFFECTS.DISSOLVE_FALL;
				break;
			default:
				message = 'Placement rejected - tetromino exploded.';
		}
	}
	if (reason === 'not_adjacent' || reason === 'no_path_to_king') {
		rejectionEffect = FAILURE_EFFECTS.DISSOLVE_FALL;
	}
	if (message) rejectionMessage = message;
	return { rejectionMessage, rejectionEffect };
}

function processPlaceTetromino() {
	const gameState = getGameState();
	if (!gameState.currentTetromino) return;
	if (gameState.isSubmittingTetrominoPlacement) return;
	gameState.isSubmittingTetrominoPlacement = true;

	const originalX = gameState.currentTetromino.position.x;
	const originalZ = gameState.currentTetromino.position.z;
	const placedTetrominoSponsor = gameState.currentTetromino.sponsor;
	cleanupGhostPiece(gameState);

	// Lightweight feedback while the server is making up its mind.
	// Previously the screen sat there silently and clicks on chess
	// pieces did nothing because the phase hadn't flipped yet.
	if (typeof showToastMessage === 'function') {
		try { showToastMessage('Placing…', 900); }
		catch (_) { /* toast is best-effort */ }
	}

	sendTetrominoPlacementToServer(gameState.currentTetromino, gameState)
		.then(response => {
			if (response && response.placedCells) {
				console.log(`Server confirmed placement of ${response.placedCells.length} cells`);
			}
			if (response && response.position) {
				console.log(`Server returned position: (${response.position.x}, ${response.position.z})`);
				showPlacementEffect(response.position.x, response.position.z, gameState);
			}

			if (response && response.success === true) {
				gameState._hasPlacedTetromino = true;
				if (Array.isArray(response.powerUpClaims) && response.powerUpClaims.length > 0) {
					const claimedIds = new Set(
						response.powerUpClaims.map(c => c && c.orbId).filter(Boolean)
					);
					gameState.powerUps = (gameState.powerUps || [])
						.filter(o => o && !claimedIds.has(o.id));
					if (typeof window.updateBoardVisuals === 'function') {
						window.updateBoardVisuals();
					}
				}
				// Sponsor popup is now click-driven only — see
				// `chessInteraction.js#showCellInfo`. We deliberately
				// do NOT call displaySponsorInfo here; users asked
				// for the ad box to appear only when they click a
				// sponsored cell.
				if (placedTetrominoSponsor) {
					console.log('Tetromino had sponsor (click cell to view):', placedTetrominoSponsor.name);
				}

				cleanupCurrentTetromino(gameState);
				if (typeof window.updateBoardVisuals === 'function') window.updateBoardVisuals();

				console.log('Placement successful. Server hasValidMoves:', response.hasValidMoves);
				cancelSkipDropTimer();
				if (response.hasValidMoves === false) {
					console.log('Server says no valid chess moves - waiting for new tetromino');
					gameState.turnPhase = 'tetris';
					cancelSkipChessTimer();
					if (typeof showToastMessage === 'function') {
						showToastMessage('No chess moves available - next piece incoming');
					}
				} else {
					gameState.turnPhase = 'chess';
					armSkipChessTimer();
					console.log('Valid chess moves available, switched to chess phase');
					if (typeof showToastMessage === 'function') {
						showToastMessage('Make your chess move!');
					}
				}
				updateNextPieceHint(gameState);

				if (typeof window.updateGameStatusDisplay === 'function') {
					window.updateGameStatusDisplay();
				}
				return;
			}

			if (response && response.success === false) {
				const isRateLimited = response.reason === 'rate_limited'
					|| response.error === 'rate_limited'
					|| response.message === 'rate_limited';

				if (isRateLimited) {
					const retryAfterMs = Number(response.retryAfterMs || 0);
					const seconds = retryAfterMs > 0
						? Math.max(0.1, Math.ceil(retryAfterMs / 100) / 10)
						: null;

					if (typeof showToastMessage === 'function') {
						showToastMessage(seconds ? `Too fast. Placing in ${seconds}s...` : 'Too fast. Placing shortly...');
					}

					if (gameState._placementRetryTimeoutId) {
						clearTimeout(gameState._placementRetryTimeoutId);
					}
					gameState._placementRetryTimeoutId = setTimeout(() => {
						if (gameState.currentTetromino && gameState.turnPhase === 'tetris') {
							queueOperation(MOVEMENT_TYPES.PLACE, {});
						}
					}, Math.max(0, retryAfterMs));
					return;
				}

				const { rejectionMessage, rejectionEffect } = rejectionMessageForReason(response);
				console.error('Server rejected placement:', response.reason || response.error || 'Unknown error');
				processExplosion(originalX, originalZ, rejectionMessage, rejectionEffect);
			}
		})
		.catch(error => {
			console.error('Error during tetromino placement:', error);
			processExplosion(originalX, originalZ, 'Placement failed - tetromino exploded.', FAILURE_EFFECTS.EXPLODE);
		})
		.finally(() => {
			gameState.isSubmittingTetrominoPlacement = false;
		});
}

function processExplosion(x, z, message, effect = FAILURE_EFFECTS.EXPLODE) {
	const gameState = getGameState();
	const shapeSnapshot = gameState.currentTetromino?.shape
		? gameState.currentTetromino.shape.map(row => row.slice())
		: null;

	if (gameState.currentTetrominoShapeGroup) {
		if (gameState.tetrominoGroup) {
			gameState.tetrominoGroup.remove(gameState.currentTetrominoShapeGroup);
		}
		gameState.currentTetrominoShapeGroup = null;
	}
	cleanupCurrentTetromino(gameState);
	cleanupGhostPiece(gameState);

	if (effect === FAILURE_EFFECTS.DISSOLVE_FALL) {
		showSandDissolveFallAnimation(x, z, shapeSnapshot, gameState);
	} else {
		showExplosionAnimation(x, z, gameState);
	}

	processCleanup(message || 'Tetromino exploded');
}

function processCleanup(message) {
	const gameState = getGameState();
	if (message && typeof showToastMessage === 'function') {
		showToastMessage(message);
	}

	console.log('Current turnPhase before cleanup:', gameState.turnPhase);
	if (gameState.currentTetrominoShapeGroup) {
		if (gameState.tetrominoGroup) {
			gameState.tetrominoGroup.remove(gameState.currentTetrominoShapeGroup);
		}
		gameState.currentTetrominoShapeGroup = null;
	}
	cleanupGhostPiece(gameState);
	gameState.currentTetromino = null;
	gameState.ghostTetromino = null;

	// After a tetromino is rejected, hand the player a fresh tetromino
	// rather than dropping them straight into a chess phase the server
	// might disagree with. The next placement response carries the
	// authoritative `hasValidMoves` flag from the server, which is what
	// actually drives the chess phase.
	console.log('Tetromino exploded — issuing replacement tetromino');
	gameState.turnPhase = 'tetris';
	const newTetromino = initializeNextTetromino(gameState);
	if (newTetromino) {
		gameState.currentTetromino = newTetromino;
		gameState.currentTetromino.heightAboveBoard = gameState.TETROMINO_START_HEIGHT || 20;
		// Spawn the 3D mesh too — without this the user sees nothing
		// after a failed placement and a stray Space-bar drop has no
		// visible source. The render call also tears down any stale
		// ghost from the previous piece.
		try { renderTetromino(gameState); }
		catch (renderErr) { console.warn('Failed to render replacement tetromino:', renderErr); }
	} else {
		// Spawn returned null — almost always because the player has
		// no king to anchor against. Don't leave the user stuck with
		// no piece and no recourse. Ask the server for a fresh one
		// (and a rescued king if necessary) and surface a clear toast
		// so they know something's being done.
		console.warn('Tetromino spawn returned null — requesting server-side recovery');
		if (typeof showToastMessage === 'function') {
			try { showToastMessage('Recovering game state…', 1500); }
			catch (_) { /* best-effort */ }
		}
		try {
			const networkManager = window.NetworkManager;
			if (networkManager && typeof networkManager.sendMessage === 'function') {
				networkManager.sendMessage('request_tetromino', {})
					.then((response) => {
						if (response && response.success && response.tetromino) {
							gameState.currentTetromino = response.tetromino;
							gameState.currentTetromino.heightAboveBoard = gameState.TETROMINO_START_HEIGHT || 20;
							try { renderTetromino(gameState); }
							catch (renderErr) { console.warn('Recovery render failed:', renderErr); }
							if (typeof window.updateBoardVisuals === 'function') window.updateBoardVisuals();
						}
					})
					.catch((err) => console.warn('Server-side tetromino recovery rejected:', err));
			}
		} catch (recoveryErr) {
			console.warn('Server-side tetromino recovery failed:', recoveryErr);
		}
	}
	cancelSkipChessTimer();
	updateNextPieceHint(gameState);

	console.log('Tetromino cleanup completed, phase:', gameState.turnPhase);
	if (typeof window.updateBoardVisuals === 'function') window.updateBoardVisuals();
	if (typeof window.updateGameStatusDisplay === 'function') window.updateGameStatusDisplay();
}

// Public API ─────────────────────────────────────────────────────────

export function queueTetrominoMovement(type, params = {}) {
	return queueOperation(type, params);
}

// User-input markers ─────────────────────────────────────────────
//
// Per the user spec the auto-fall must:
//   1. Stay paused until the player makes their first move; then
//   2. Pause for 0.5 s after every manual move/rotate so they can
//      shuffle the piece a long way without it falling away under
//      them, but
//   3. Run *concurrently* with the regular 1 s fall countdown —
//      i.e. rapid moves don't keep stacking the pause, they only
//      delay the next fall by 0.5 s from the LAST move.
//
// `markUserInteraction` stamps the currentTetromino's `fallStarted`
// and `lastMoveTime` fields; the game loop reads both. Only the
// USER-FACING exports below call it — the auto-fall (`moveTetrominoY`
// from the game loop) deliberately doesn't, because that would make
// the piece its own first input.

function markUserInteraction() {
	const gameState = getGameState();
	const tetro = gameState && gameState.currentTetromino;
	if (!tetro) return;
	tetro.fallStarted = true;
	tetro.lastMoveTime = Date.now();
}

export function moveTetrominoX(dir) {
	markUserInteraction();
	return queueOperation(MOVEMENT_TYPES.MOVE_X, { dir });
}

export function moveTetrominoZ(dir) {
	markUserInteraction();
	return queueOperation(MOVEMENT_TYPES.MOVE_Z, { dir });
}

export function moveTetrominoY(height, isRelative = true) {
	return queueOperation(MOVEMENT_TYPES.MOVE_Y, { height, isRelative });
}

export function rotateTetromino(dir) {
	markUserInteraction();
	return queueOperation(MOVEMENT_TYPES.ROTATE, { dir });
}

export function hardDropTetromino() {
	const gameState = getGameState();
	if (gameState?.currentTetromino) {
		// Hard drop unambiguously expresses intent — start the fall
		// state machine but DON'T set lastMoveTime, because we don't
		// want a pause after a one-shot hard drop.
		gameState.currentTetromino.fallStarted = true;
	}
	return queueOperation(MOVEMENT_TYPES.HARD_DROP, {});
}

export function cleanupTetrominoAndTransitionToChess(_gameState, message, x, z, effect = FAILURE_EFFECTS.EXPLODE) {
	return queueOperation(MOVEMENT_TYPES.EXPLODE, { x, z, message, effect });
}

export function enhancedPlaceTetromino() {
	return queueOperation(MOVEMENT_TYPES.PLACE, {});
}

// Wire the network module's failure handler into our explosion logic
// so a `tetrominoFailed` socket event triggers the same animation path
// as a server-rejected placement.
setPlacementFailureHandler((x, z, message, effect) => {
	queueOperation(MOVEMENT_TYPES.EXPLODE, {
		x, z, message,
		effect: effect === 'DISSOLVE_FALL' ? FAILURE_EFFECTS.DISSOLVE_FALL : FAILURE_EFFECTS.EXPLODE,
	});
});
